import { selectFormById } from "@/features/forms/api/selectFormById";
import { isUuid } from "@/services/isUuid";
import type { EntryViewT, FormViewT, StatefulDiaryT, StatefulTrackerViewT } from "@patient-watch/types";
import { DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";

function inputLabelMapFromForm(form: FormViewT): Map<string, string> {
    const map = new Map<string, string>();
    for (const page of form.pages ?? []) {
        for (const input of page.inputs ?? []) {
            if (input.input_id && input.input_label) {
                map.set(input.input_id, input.input_label);
            }
        }
    }
    return map;
}

function localizeEntryView(entry: EntryViewT, form: FormViewT, labels: Map<string, string>): EntryViewT {
    const formName = form.form_name;
    return {
        ...entry,
        entry_values: (entry.entry_values ?? []).map((value) => ({
            ...value,
            ...(formName ? { form_name: formName } : {}),
            input_label: labels.get(value.input_id) ?? value.input_label,
            json_value: Array.isArray(value.json_value)
                ? value.json_value.map((row) => ({
                      ...row,
                      input_label: (row.input_id && labels.get(row.input_id)) || row.input_label,
                  }))
                : value.json_value,
        })) as EntryViewT["entry_values"],
    };
}

/** Overlay locale-specific questionnaire copy. Tracker form_id stays canonical. */
export async function localizeDiaryTrackerForms(
    diary: StatefulDiaryT,
    locale: SupportedLocale = DEFAULT_LOCALE
): Promise<StatefulDiaryT> {
    const trackers = diary.trackers_view ?? [];
    if (trackers.length === 0 || locale === DEFAULT_LOCALE) {
        return diary;
    }

    const formIds = [...new Set(trackers.map((tracker) => tracker.form_id).filter((id): id is string => isUuid(id)))];
    const localizedForms = await Promise.all(formIds.map((formId) => selectFormById(formId, locale)));
    const formsById = new Map(localizedForms.map((form) => [form.form_id, form]));

    const localizedTrackers = trackers.map((tracker) => {
        if (!tracker.form_id) {
            return tracker;
        }

        const localizedForm = formsById.get(tracker.form_id);
        if (!localizedForm) {
            return tracker;
        }

        const labels = inputLabelMapFromForm(localizedForm);
        return {
            ...tracker,
            form: localizedForm,
            entries_view: (tracker.entries_view ?? []).map((entry) => localizeEntryView(entry, localizedForm, labels)),
        } as StatefulTrackerViewT;
    });

    return {
        ...diary,
        trackers_view: localizedTrackers as StatefulDiaryT["trackers_view"],
    };
}
